import { useMutation, useQueries } from '@tanstack/react-query';
import { useState } from 'react';

import { queryKeys } from '~/api/queryKeys';

import { HEALTH_AGENTS, HEALTH_SERVICES } from '../constants/health-services';
import type { HealthServiceInfo } from '../constants/health-services';
import { getServiceById } from '../services/health.service';
import type { ServiceHealth } from '../types/health.types';
import { recordLatency } from '../utils/latency-history';

/** How often auto (always-on) services re-probe. */
export const HEALTH_REFRESH_INTERVAL_MS = 30_000;

/** One service's live state on the Health page: its static info plus the
 *  independent query result (fetching / data / error). Un-probed = no data,
 *  no error, not fetching. */
export interface ServiceHealthEntry {
  info: HealthServiceInfo;
  data: ServiceHealth | undefined;
  isFetching: boolean;
  isError: boolean;
}

interface UseServicesHealthOptions {
  /** `auto` = probe on mount and poll every 30s (always-on core services);
   *  otherwise nothing runs until `wake(id)` / `wakeAll()` (on-demand agents). */
  auto: boolean;
}

/**
 * Fans out one independent query per service (`GET /health/service/{id}`, which
 * also cold-starts the target). In `auto` mode each probe runs on mount and
 * re-polls every 30s; otherwise probing is on demand via `wake`/`wakeAll`,
 * avoiding a burst of cold starts on page load.
 */
export function useServicesHealth(
  services: HealthServiceInfo[],
  { auto }: UseServicesHealthOptions,
) {
  const results = useQueries({
    queries: services.map((svc) => ({
      queryKey: queryKeys.serviceHealth.service(svc.id),
      // Record the latency sample as each probe resolves so the tile sparkline
      // builds up over successive probes (queryFn runs once per fetch).
      queryFn: async () => {
        const service = await getServiceById(svc.id);
        recordLatency(svc.id, service.latencyMs);
        return service;
      },
      enabled: auto,
      refetchInterval: auto ? HEALTH_REFRESH_INTERVAL_MS : (false as const),
      refetchIntervalInBackground: auto,
      refetchOnWindowFocus: auto,
      staleTime: 0,
      gcTime: 0,
      // A 404 (service unconfigured in this env) is a real answer, not worth retrying.
      retry: false,
    })),
  });

  const entries: ServiceHealthEntry[] = services.map((info, i) => {
    const q = results[i];
    return {
      info,
      data: q?.data,
      isFetching: q?.isFetching ?? false,
      isError: q?.isError ?? false,
    };
  });

  // Wake (or re-check) a single service by id — a one-shot probe on demand.
  const wake = (id: string) => {
    const i = services.findIndex((s) => s.id === id);
    if (i >= 0) void results[i]?.refetch();
  };

  // Probe every service in this group at once ("Wake all" / refresh).
  const wakeAll = () => {
    results.forEach((q) => {
      void q.refetch();
    });
  };

  // "Refreshing" = a fetch is in flight after the initial load has settled, so
  // an auto section's refresh control spins only on a genuine refresh.
  const isInitialLoading = auto && results.some((q) => q.isLoading);
  const isRefreshing = results.some((q) => q.isFetching) && !isInitialLoading;
  // A bulk wake is in flight only while EVERY service is probing — so waking a
  // single card doesn't make the "Wake all" control look busy.
  const isWakingAll = results.length > 0 && results.every((q) => q.isFetching);

  return { entries, wake, wakeAll, refresh: wakeAll, isRefreshing, isWakingAll };
}

/** Every core service + agent probed by a single "wake everything" run. */
const WAKE_ALL_ENTRIES: HealthServiceInfo[] = [...HEALTH_SERVICES, ...HEALTH_AGENTS];

/**
 * One-shot, imperative "wake everything" trigger for a manual admin action
 * (e.g. a sidebar button) — probes every core service AND every agent via
 * `GET /health/service/{id}`, the exact same per-service gateway call the
 * Health page's own auto-section and "Wake all" button use (confirmed against
 * the live page: this is what actually reaches each service, including ones
 * like `observability` with no direct browser-facing proxy — the gateway
 * routes internally). Unlike `useServicesHealth`, this doesn't keep any query
 * live; each probe is best-effort (a failure still counts toward `completed`)
 * so one down service doesn't block or skew the rest.
 *
 * `completed`/`total` track live progress across the run so a caller can show
 * a small "N of M" indicator while the mutation is pending.
 */
export function useWakeAllServices() {
  const [completed, setCompleted] = useState(0);

  const mutation = useMutation({
    mutationFn: async () => {
      setCompleted(0);
      await Promise.all(
        WAKE_ALL_ENTRIES.map(async (entry) => {
          try {
            await getServiceById(entry.id);
          } finally {
            setCompleted((n) => n + 1);
          }
        }),
      );
    },
  });

  return { ...mutation, completed, total: WAKE_ALL_ENTRIES.length };
}
